/* global __TEST__ */
import ko from "knockout";
import _ from "lodash";
import { flow, map, uniqBy } from "lodash/fp";
import moment from "moment";
import $ from "jquery";
import Promise from "bluebird";
import config from "config";
import authService from "services/authService";
import apiCore from "services/apiCore";
import ExponentialIntervalGenerator from "util/ExponentialIntervalGenerator";
import pubSub from "util/PubSub";
import LOG from "services/loggingService";

const NOTIFICATION_URI = "/notifications/channels";
const HB_TIMER_EXPIRATION = 35000;
let hbTimer;

let connection;
const defaults = {
    useWebSocket: true,
    maxReconnectInterval: 5 * 1000, //5 seconds
    nsRoot: config,
    reconnectTimeout: 30 * 60 * 1000, //30 minutes
};

const options = $.extend({}, defaults, config.notifications || {});

const exponentialBackoff = new ExponentialIntervalGenerator(
    options.maxReconnectInterval
);
let reconnectTimeoutObj = null;
let isConnectionSetupSettled = false;
const randomId = ko.observable();
let lastConnectionTime = moment.now();
let connectUri;

/**
 * Connect to a notification stream via websockets.
 * @param {object} params parameters required for connection
 * @return {WebSocket} The newly created websocket object
 */
const connect = function () {
    if (connectUri) {
        connection = new WebSocket(connectUri);

        connection.onopen = function () {
            resetHeartbeatExpirationTimer();

            LOG.debug("WS: open");
            notificationService.connected(true);

            // we've created a connection, so lets reset the interval if we lose it
            // again in the future
            exponentialBackoff.reset();

            if (reconnectTimeoutObj) {
                // remove the timeout callback if we connect before the timeout has been
                // executed
                clearTimeout(reconnectTimeoutObj);
            }

            lastConnectionTime = moment.now();
        };

        connection.onclose = function () {
            LOG.debug("WS: close");
            clearTimeout(hbTimer);

            //flag as disconnected
            notificationService.connected(false);

            //re-connect if we're still logged in
            if (authService.principalId()) {
                if (
                    moment.duration(moment().diff(lastConnectionTime)) <
                    options.reconnectTimeout
                ) {
                    const reconnectInterval = exponentialBackoff.getInterval();
                    exponentialBackoff.fail();
                    LOG.debug(
                        "WS: reconnect attempt in ",
                        reconnectInterval,
                        arguments
                    );
                    reconnectTimeoutObj = setTimeout(
                        connect,
                        reconnectInterval
                    );
                } else {
                    if (reconnectTimeoutObj) {
                        clearTimeout(reconnectTimeoutObj);
                        reconnectTimeoutObj = null;
                    }
                    LOG.debug(
                        "WS: unable to reconnect within " +
                            options.reconnectTimeout +
                            "ms. Pausing reconnection attempts."
                    );
                }
            }
        };

        connection.onerror = function (event) {
            LOG.warn("WS: error", event); //browser will also trigger close
        };

        connection.onmessage = function (messageEvent) {
            const data = messageEvent.data;
            if (data) {
                try {
                    const json = JSON.parse(data);
                    if (json.topicName === "channel.metadata") {
                        LOG.debug("WS Status:", json);
                        resetHeartbeatExpirationTimer();
                    } else {
                        let eventBody;
                        const options = {};

                        try {
                            eventBody = json.eventBody;

                            if (_.has(json, "metadata")) {
                                options.metadata = json.metadata;
                            }

                            LOG.info(
                                "WS Topic: %s,  Body: %O",
                                json.topicName,
                                eventBody
                            );
                        } catch (err) {
                            LOG.error(
                                "WS Error: Unable to parse event body.",
                                err
                            );
                            LOG.error(err.message, err.stack);
                            throw err;
                        }

                        pubSub
                            .Topic(json.topicName)
                            .publish(eventBody, options);
                    }
                } catch (err) {
                    LOG.error("WS Error: Unable to parse envelope.", err);
                    throw err;
                }
            }
        };
    }
};

const disconnect = function () {
    if (!__TEST__) {
        LOG.debug("WS: Attempt to disconnect connection");
    }
    if (connection) {
        connection.close();
    }
    pubSub.clearAllTopics();
    if (
        subscriptionPromise &&
        typeof subscriptionPromise.cancel === "function"
    ) {
        subscriptionPromise.cancel();
    }
    if (activeSubscriptions) {
        activeSubscriptions.removeAll();
    }
};

const resetHeartbeatExpirationTimer = function () {
    if (hbTimer) {
        clearTimeout(hbTimer);
    }

    hbTimer = setTimeout(function () {
        LOG.warn(
            "WS: heartbeat timer expired for user",
            ko.unwrap(authService.principalId)
        );
        if (connection) {
            connection.close();
        }
    }, HB_TIMER_EXPIRATION);
};

// Track all active subscription requests. When consumers subscribe or unsubscribe,
// notificationService will update this observableArray, triggering a PUT of all active
// subscription topics, ensuring the requested collection is consistent with all changes
const activeSubscriptions = ko.observableArray();

// Promise tracks pending requests to PUT all active subscription requests
let subscriptionPromise = Promise.resolve();

let consumerPromiseResolvers = [];

function getConnectUri() {
    return apiCore.v2.post(NOTIFICATION_URI).then(function (response) {
        randomId(response.id);
        connectUri = response.connectUri;
    });
}

function setupConnection() {
    isConnectionSetupSettled = false;
    const userId = authService.principalId();
    if (userId) {
        // post to get URI
        getConnectUri()
            .then(connect)
            .catch(function (e) {
                LOG.error("Unable to connect to carrier pigeon", e);
                //reject all pending consumer promises and clear them
                _.forEach(consumerPromiseResolvers, function (resolver) {
                    resolver.reject(e);
                });
                consumerPromiseResolvers = [];
            })
            .finally(function () {
                isConnectionSetupSettled = true;
            });
    } else {
        isConnectionSetupSettled = true;
        disconnect();
    }
}

function start() {
    // Check how long since our last connection. If it's been awhile,
    // reset with a new channel
    $(window).on("focus.checkConnectionTime", function () {
        const now = moment.now();
        if (
            moment.duration(moment(now).diff(lastConnectionTime)) >
            options.reconnectTimeout
        ) {
            lastConnectionTime = now;
            setupConnection();
        }
    });

    //Connect if we're already logged in &
    //Trigger the asynchronous connect/disconnect actions on login/logout
    ko.computed(setupConnection);

    // Computed to PUT all the active subscription requests when the collection changes.
    // Rate Limited to 200ms after changes to the collection have stopped, to debounce
    // in case a consumer (or multiple consumers) make simultaneous (or multiple subsequent)
    // changes to subscriptions
    ko.computed(function () {
        if (
            subscriptionPromise &&
            typeof subscriptionPromise.cancel === "function"
        ) {
            subscriptionPromise.cancel();
        }
        if (activeSubscriptions().length > 0 && randomId()) {
            // unique ids from active subscriptions
            const subscriptions = flow(
                map(({ id }) => ({ id })),
                uniqBy("id")
            )(activeSubscriptions());

            subscriptionPromise = apiCore.v2
                .put(
                    NOTIFICATION_URI + "/" + randomId() + "/subscriptions",
                    subscriptions
                )
                .then(function () {
                    //resolve all pending consumer promises and clear them
                    _.forEach(consumerPromiseResolvers, function (resolver) {
                        resolver.resolve();
                    });
                    consumerPromiseResolvers = [];
                    LOG.info(
                        "Subscribed to multiple topics.",
                        activeSubscriptions()
                    );
                })
                .catch(Promise.CancellationError, _.noop)
                .catch(function (error) {
                    //reject all pending consumer promises and clear them
                    _.forEach(consumerPromiseResolvers, function (resolver) {
                        resolver.reject(error);
                    });
                    consumerPromiseResolvers = [];
                    LOG.error("Failed to subscribe to multiple topics.", {
                        activeSubscriptions: activeSubscriptions(),
                        error,
                    });
                    throw error;
                })
                .finally(function () {
                    subscriptionPromise = null;
                });
        } else if (
            activeSubscriptions().length === 0 ||
            isConnectionSetupSettled
        ) {
            _.forEach(consumerPromiseResolvers, function (resolver) {
                if (activeSubscriptions().length === 0) {
                    resolver.resolve();
                } else {
                    resolver.reject(
                        new Error("Not connected to notification service")
                    );
                }
            });
            consumerPromiseResolvers = [];
        }
    }).extend({ rateLimit: { timeout: 200, method: "notifyWhenChangesStop" } });
}

//helper function to defer a consumer promise
const defer = function () {
    let resolve, reject;
    const promise = new Promise(function (resolveFn, rejectFn) {
        resolve = resolveFn;
        reject = rejectFn;
    });
    consumerPromiseResolvers.push({
        resolve: resolve,
        reject: reject,
    });
    return promise;
};

const notificationService = {
    start: _.once(start),

    /**
     * An observable attribute that indicates whether the client is connected to the
     * notification service backend.
     *
     * @type {Observable Boolean} true if connected, false otherwise.
     */
    connected: ko.observable(false),

    /**
     * Subscribe a callback to events that are published to a topic key.
     *
     * @param  {String}   topic - A string indicating the key name of a topic that
     *  will receive notification events.
     * @param  {Function} callback - A functions that will execute when a notification
     *  event is published to the provided topic.
     * @return {Promise} A promise that is resolved when the subscription is
     *  successful
     */
    subscribe: function (topicId, callback) {
        const newSubscription = { id: topicId, callback: callback };
        const existingSubscription = _.find(
            activeSubscriptions.peek(),
            newSubscription
        );
        if (!existingSubscription) {
            activeSubscriptions.push(newSubscription);
        }

        return defer().then(function () {
            pubSub.Topic(topicId).subscribe(callback);
        });
    },

    /**
     * Subscibe a to collection of topics with a callback for each one.
     *
     * @param  {Map} batchObj A map that contains the set of topics the should be
     *  subscribed to and the callback that should execute for each one.
     *
     *  The key of a map entry will be used as the name of the topic to subscribe to.
     *
     *  The value of a map entry is expected to be a function that will be executed
     *  whenever an event is published to the topic.
     *
     *  Example:
     *
     *      { "t1": function () {} }
     *
     *  In the example, "t1" will be used as the topic to subscribe to and the value
     *  function will be used as the callback.
     *
     * @return {Promise} A promise object that is resolved once all topics have
     *  successfully subscribed with the service.
     */
    batchSubscribe: function (batchObj) {
        const newSubscriptions = [];

        _.forIn(batchObj, function (callback, topicId) {
            const newSubscription = { id: topicId, callback: callback };
            const existingSubscription = _.find(
                activeSubscriptions.peek(),
                newSubscription
            );
            if (!existingSubscription) {
                newSubscriptions.push(newSubscription);
            }
        });
        ko.utils.arrayPushAll(activeSubscriptions, newSubscriptions);

        return defer().then(function () {
            _.forEach(newSubscriptions, function (topic) {
                const callback = batchObj[topic.id];
                pubSub.Topic(topic.id).subscribe(callback);
            });
        });
    },

    /**
     * Unsubscribe a callback from events that are published to a topic key.
     *
     * @param  {String}   topic - A string indicating the key name of the topic to
     *  remove the subcribed callback from.
     * @param  {Function} callback - A function that has subscribed to the provided
     *  topic that will stop being executed when notification events are sent to the
     *  topic.
     * @return {Promise} A promise that is resolved when the unsubscription is
     *  successful
     */
    unsubscribe: function (topicId, callback) {
        LOG.info('Unsubscribing from "' + topicId + '".');

        pubSub.Topic(topicId).unsubscribe(callback);

        const existingSubscription = _.find(activeSubscriptions.peek(), {
            id: topicId,
            callback: callback,
        });
        if (existingSubscription) {
            activeSubscriptions.remove(existingSubscription);
        }

        return defer();
    },

    /**
     * Unsubscribe a collection of callbacks from a set of topics.
     *
     * @param  {Map} batchObj A map that contains the set of topics the should be
     *  unsubscribed to and the callback that should be removed for each one.
     *
     *  The key of a map entry will be used as the name of the topic to unsubscribe
     *  from.
     *
     *  The value of a map entry is expected to be a function that will be removed
     *  from the topic.
     *
     *  Example:
     *
     *      { "t1": function () {} }
     *
     *  In the example, "t1" will be used as the topic key to unsubscribe from and the
     *  function should be an instance of the callback function to remove from the
     *  list of callbacks for a topic.
     *
     * @return {Promise} A promise object that is resolved once all topics have
     *  successfully subscribed with the service.
     */
    batchUnsubscribe: function (batchObj) {
        const subscriptions = activeSubscriptions.peek();

        _.forIn(batchObj, function (callback, topicId) {
            pubSub.Topic(topicId).unsubscribe(callback);

            const existingSubscription = _.find(subscriptions, {
                id: topicId,
                callback: callback,
            });
            if (existingSubscription) {
                _.remove(subscriptions, existingSubscription);
            }
        });

        activeSubscriptions(subscriptions);

        return defer();
    },
};

notificationService.start();

export default notificationService;
